import { useState, useEffect, useCallback } from 'react';
import { useDemands } from '@contexts/DemandsContext';
import { useConfig } from '@contexts/ConfigContext';
import { useAuth } from '@contexts/AuthContext';
import { getDateRangeFromType } from '@components/DateRangeFilter';
import { IDemandsData, IDemandsStats, IDemand, IKanbanColumn, IQRCode } from '@/types';
import { apiService } from '@/services/api';
import { normalizeDateFromApi } from '@/utils/dateHelpers';
import { getEntityId } from '@/utils/entityId';
import { classifyDemandDeadlineStatus } from '@/features/reports/reportsPerformanceCalculations';

/**
 * Hook responsável por:
 * - Sincronizar o período (dateRange) de demandas com as configurações do sistema
 * - Aplicar filtros por cliente e período nas demandas
 * - Calcular estatísticas agregadas a partir dos dados filtrados
 *
 * Mantém a lógica de negócio de filtros e estatísticas fora do componente visual.
 */
export const useDemandFilters = () => {
  const {
    demandsData,
    selectedClients,
    dateRange,
    setDateRange,
    getStoredDateRange,
    demandsDateType,
    selectedCategories,
    selectedStatuses,
    selectedQrCodeIdentifiers,
    selectedPriorities,
    selectedAssigneeIds,
    selectedProviderIds,
    searchText,
    overdueOnly,
    checklistPendingDemandIds,
  } = useDemands();

  const { config, loading: configLoading } = useConfig();
  const { user: currentUser } = useAuth();

  const [filteredData, setFilteredData] = useState<IDemandsData[]>([]);
  const [stats, setStats] = useState<IDemandsStats | null>(null);
  const [periodInitialized, setPeriodInitialized] = useState(false);
  const [lastUserId, setLastUserId] = useState<string | null>(null);
  const [qrCodeMap, setQrCodeMap] = useState<Map<string, IQRCode>>(new Map());

  // Resetar período inicializado quando o usuário logado muda
  useEffect(() => {
    if (currentUser?._id && currentUser._id !== lastUserId) {
      setPeriodInitialized(false);
      setLastUserId(currentUser._id);
    }
  }, [currentUser?._id, lastUserId]);

  // Aplicar período (salvo ou padrão) quando as configurações estiverem carregadas
  useEffect(() => {
    if (configLoading) {
      return;
    }

    if (config && config.demandManagement && !periodInitialized) {
      const storedRange = getStoredDateRange('demands');

      if (storedRange) {
        if (storedRange.type !== 'custom') {
          const recalculatedRange = getDateRangeFromType(storedRange.type);
          setDateRange(recalculatedRange, 'demands');
        } else {
          setDateRange(storedRange, 'demands');
        }
      } else {
        const defaultPeriod = config.demandManagement.defaultPeriod || 'all';
        const newDateRange = getDateRangeFromType(defaultPeriod);
        setDateRange(newDateRange, 'demands');
      }

      setPeriodInitialized(true);
    }
  }, [config, configLoading, periodInitialized, getStoredDateRange, setDateRange]);

  // Calcular estatísticas dinâmicas baseadas nos dados filtrados
  const calculateDynamicStats = (data: IDemandsData[]): IDemandsStats => {
    let totalDemands = 0;
    let openDemands = 0;
    let inProgressDemands = 0;
    let completedDemands = 0;
    let cancelledDemands = 0;
    const totalClients = data.length;

    data.forEach((client) => {
      const demands = (Object.values(client.demands) as IDemand[][]).flat();
      totalDemands += demands.length;
      openDemands += client.demands.pending?.length || 0;
      inProgressDemands += client.demands.in_progress?.length || 0;
      completedDemands += client.demands.completed?.length || 0;
      cancelledDemands += client.demands.cancelled?.length || 0;
    });

    // Tempo médio de resolução (mantém a lógica atual simulada)
    const avgResolutionTime =
      completedDemands > 0 ? Math.round((Math.random() * 5 + 1) * 10) / 10 : 0;

    return {
      totalDemands,
      openDemands,
      inProgressDemands,
      completedDemands,
      cancelledDemands,
      avgResolutionTime,
      totalClients,
      activeUsers: 1, // Simulado por enquanto
    };
  };

  // Buscar QR codes quando necessário para o filtro
  useEffect(() => {
    if (!selectedQrCodeIdentifiers?.length) {
      setQrCodeMap(new Map());
      return;
    }

    // Coletar todos os qrCodeIds únicos das demandas
      const allQrCodeIds = new Set<string>();
      demandsData.forEach((clientData) => {
        Object.values(clientData.demands).forEach((demands) => {
          demands.forEach((demand: IDemand) => {
          if (demand.qrCodeId) {
            const qrCodeIdStr = typeof demand.qrCodeId === 'string' 
              ? demand.qrCodeId 
              : (demand.qrCodeId as unknown as { _id?: string; id?: string })?._id || (demand.qrCodeId as unknown as { _id?: string; id?: string })?.id || '';
            if (qrCodeIdStr) {
              allQrCodeIds.add(qrCodeIdStr);
            }
          }
        });
      });
    });

    // Buscar todos os QR codes de uma vez
    const fetchQRCodes = async () => {
      const map = new Map<string, IQRCode>();
      const promises = Array.from(allQrCodeIds).map(async (qrCodeId) => {
        try {
          const response = await apiService.getQRCode(qrCodeId);
          if (response.data.success && response.data.data) {
            map.set(qrCodeId, response.data.data);
          }
        } catch (error) {
          // Silently fail - QR code não encontrado
        }
      });
      await Promise.all(promises);
      setQrCodeMap(map);
    };

    if (allQrCodeIds.size > 0) {
      fetchQRCodes();
    } else {
      setQrCodeMap(new Map());
    }
  }, [selectedQrCodeIdentifiers, demandsData]);

  // Função auxiliar para aplicar todos os filtros em uma demanda
  const shouldIncludeDemand = useCallback((demand: IDemand): boolean => {
    // Função auxiliar para obter a data usada no filtro de período
    // Respeita o demandsDateType: createdAt ou estimatedCompletion
    const getDemandFilterDate = (demand: IDemand): Date | null => {
      if (demandsDateType === 'estimatedCompletion') {
        // Prazo Estimado: demandas sem estimatedCompletion (ex.: criadas por QR code) são excluídas
        if (!demand.estimatedCompletion) {
          return null;
        }
        // Usar normalizeDateFromApi para tratar corretamente o timezone UTC do banco
        const normalized = normalizeDateFromApi(demand.estimatedCompletion);
        if (!normalized) {
          return null;
        }
        return normalized;
      } else {
        // Data de Abertura: filtrar pela createdAt
        // createdAt também vem em UTC, então normalizar também
        const normalized = normalizeDateFromApi(demand.createdAt);
        if (!normalized) {
          return new Date(demand.createdAt); // Fallback se normalização falhar
        }
        return normalized;
      }
    };

    // Filtro por data
    if (dateRange.startDate && dateRange.endDate) {
      const demandDateOrNull = getDemandFilterDate(demand);
      // Excluir demandas sem data válida (ex.: sem estimatedCompletion ao filtrar por Prazo Estimado)
      if (!demandDateOrNull) {
        return false;
      }

      const normalizeDateForComparison = (date: Date): Date => {
        const normalized = new Date(date);
        normalized.setHours(0, 0, 0, 0);
        return normalized;
      };

      const startDateNormalized = normalizeDateForComparison(dateRange.startDate);
      const endDateNormalized = normalizeDateForComparison(dateRange.endDate);
      endDateNormalized.setHours(23, 59, 59, 999);
      const demandDate = normalizeDateForComparison(demandDateOrNull);

      if (demandDate < startDateNormalized || demandDate > endDateNormalized) {
        return false;
      }
    }

    // Filtro por categoria
    if (selectedCategories?.length > 0 && !selectedCategories.includes(demand.category)) {
      return false;
    }

    // Filtro por situação (demand.status está em inglês: pending, in_progress, completed, cancelled)
    if (selectedStatuses?.length > 0 && !selectedStatuses.includes(demand.status)) {
      return false;
    }

    // Filtro por prioridade
    if (selectedPriorities?.length > 0 && !selectedPriorities.includes(demand.priority)) {
      return false;
    }

    // Filtro por QR code identifier
    if (selectedQrCodeIdentifiers?.length > 0) {
      if (!demand.qrCodeId) {
        return false;
      }
      const qrCodeIdStr = typeof demand.qrCodeId === 'string' 
        ? demand.qrCodeId 
        : (demand.qrCodeId as unknown as { _id?: string; id?: string })?._id || (demand.qrCodeId as unknown as { _id?: string; id?: string })?.id || '';
      
      if (!qrCodeIdStr) {
        return false;
      }

      const qrCode = qrCodeMap.get(qrCodeIdStr);
      if (!qrCode || !selectedQrCodeIdentifiers.includes(qrCode.identifier)) {
        return false;
      }
    }

    // Filtro por responsável (item 10)
    if (selectedAssigneeIds?.length > 0) {
      const assigneeId = getEntityId(demand.assigneeId);
      if (!assigneeId || !selectedAssigneeIds.includes(assigneeId)) {
        return false;
      }
    }

    // Filtro por prestador (item 10)
    if (selectedProviderIds?.length > 0) {
      const providerId = getEntityId(demand.providerId);
      if (!providerId || !selectedProviderIds.includes(providerId)) {
        return false;
      }
    }

    // Busca por texto (número/descrição) — mesmo critério do app mobile (demands.tsx).
    if (searchText?.trim()) {
      const term = searchText.trim().toLowerCase();
      const matchesNumber = demand.demandNumber?.toLowerCase().includes(term);
      const matchesDescription = demand.description?.toLowerCase().includes(term);
      if (!matchesNumber && !matchesDescription) {
        return false;
      }
    }

    // Atalho "Demandas atrasadas" do sino de notificações (pedido do usuário, 05/09) — mesma
    // definição de "atrasada" usada no cálculo do badge (status em aberto/andamento + prazo
    // vencido), pra o board e o sino nunca divergirem sobre o que conta.
    if (overdueOnly && classifyDemandDeadlineStatus(demand).kind !== 'in_progress_late') {
      return false;
    }

    // Atalho "Checklists pendentes" do sino — `checklistPendingDemandIds` vem de
    // GET /admin/notifications/pending-checklist-demand-ids (checklist não é um campo da Demand).
    if (checklistPendingDemandIds !== null && !checklistPendingDemandIds.includes(demand._id)) {
      return false;
    }

    return true;
  }, [
    dateRange,
    demandsDateType,
    selectedCategories,
    selectedStatuses,
    selectedPriorities,
    selectedQrCodeIdentifiers,
    selectedAssigneeIds,
    selectedProviderIds,
    qrCodeMap,
    searchText,
    overdueOnly,
    checklistPendingDemandIds,
  ]);

  // Aplicar filtros sempre que selectedClients, demandsData, dateRange, ou outros filtros mudarem
  useEffect(() => {
    let filtered = demandsData.filter((client: IDemandsData) =>
      selectedClients.includes(client.client.id),
    );

    // Aplicar todos os filtros nas demandas
    filtered = filtered.map((clientData) => {
      const filteredDemands: IKanbanColumn = {
        pending: clientData.demands.pending.filter(shouldIncludeDemand),
        in_progress: clientData.demands.in_progress.filter(shouldIncludeDemand),
        completed: clientData.demands.completed.filter(shouldIncludeDemand),
        cancelled: clientData.demands.cancelled.filter(shouldIncludeDemand),
      };

      const totalDemands =
        filteredDemands.pending.length +
        filteredDemands.in_progress.length +
        filteredDemands.completed.length +
        filteredDemands.cancelled.length;

      return {
        ...clientData,
        demands: filteredDemands,
        totalDemands,
      };
    });

    setFilteredData(filtered);
  }, [selectedClients, demandsData, shouldIncludeDemand]);

  // Recalcular estatísticas quando os dados filtrados mudarem
  useEffect(() => {
    if (filteredData.length > 0) {
      const dynamicStats = calculateDynamicStats(filteredData);
      setStats(dynamicStats);
    } else {
      setStats(null);
    }
  }, [filteredData]);

  return {
    filteredData,
    stats,
    config,
    configLoading,
  };
};


